import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const AuthCallback = () => {
  const navigate = useNavigate();
  const { setAuthData, token } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // useRef prevents double-processing under StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');
      
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get('code');

      if (!sessionId && !code) {
        toast.error('Invalid authentication callback');
        navigate('/login', { replace: true });
        return;
      }

      // Check if this is a "link Google account" flow
      const isLinkMode = localStorage.getItem('google_link_mode') === 'true';
      const storedToken = localStorage.getItem('token');

      if (isLinkMode && storedToken) {
        localStorage.removeItem('google_link_mode');
        try {
          await axios.post(
            `${API}/auth/link-google`,
            { 
              session_id: sessionId,
              code: code,
              redirect_uri: window.location.origin + '/auth/callback'
            },
            { headers: { Authorization: `Bearer ${storedToken}` } }
          );
          toast.success('Google account linked successfully!');
          navigate('/settings', { replace: true });
        } catch (error) {
          const detail = error.response?.data?.detail || 'Failed to link Google account';
          toast.error(detail);
          navigate('/settings', { replace: true });
        }
        return;
      }

      // Normal login/register flow
      try {
        const response = await axios.post(`${API}/auth/google-callback`, {
          session_id: sessionId,
          code: code,
          redirect_uri: window.location.origin + '/auth/callback'
        });

        const { access_token, user, is_new_user, bonus_points } = response.data;

        // Set auth data (same as normal login)
        setAuthData(access_token, user);

        if (is_new_user) {
          toast.success(`Welcome to PropertyShop! You earned ${bonus_points || 0} bonus points!`);
        } else {
          toast.success('Signed in with Google successfully!');
        }

        // Clean URL and navigate to dashboard
        navigate('/dashboard', { replace: true });
      } catch (error) {
        const detail = error.response?.data?.detail || 'Google sign-in failed';
        toast.error(detail);
        navigate('/login', { replace: true });
      }
    };

    processGoogleAuth();
  }, [navigate, setAuthData, token]);

  return (
    <div className="min-h-screen bg-[#212A31] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-[var(--brand)] border-t-transparent rounded-full animate-spin" />
        <span className="text-gray-400 text-sm">
          {localStorage.getItem('google_link_mode') === 'true' ? 'Linking Google account...' : 'Signing in with Google...'}
        </span>
      </div>
    </div>
  );
};
